import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styles from "./TransactionHistoryTable.module.css";
import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";

/* Loading skeleton for the transaction history table 
  This component displays a skeleton loader while the transaction data is being fetched.
  @param isMobileView - A boolean indicating if the view is mobile or not.
  @returns A ReactElement representing the loading skeleton for the TransactionHistoryTable.
*/
export const TransactionHistoryTableLoadingSkeleton = ({
  isMobileView = false,
}: {
  isMobileView: boolean;
}) => {
  const { t } = useTranslation();
  return isMobileView ? (
    <div className={styles.loadingSkeletonMobile}>
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className={styles.loadingSkeletonCard}>
          <div className={styles.transactionHead}>
            <SkeletonLoader
              height={6}
              rounding="sm"
              variant="rectangular"
              extraClasses={styles["m-loader"]}
            />
            <SkeletonLoader
              height={6}
              rounding="sm"
              variant="rectangular"
              extraClasses={styles["m-loader"]}
            />
          </div>
          <div className={styles.dateAndStatus}>
            <SkeletonLoader
              height={6}
              rounding="sm"
              variant="rectangular"
              extraClasses={styles["l-loader"]}
            />
            <SkeletonLoader
              height={6}
              width={20}
              rounding="lg"
              variant="rectangular"
            />
          </div>
          <div className={styles.transactionType}>
            <SkeletonLoader
              height={6}
              rounding="sm"
              variant="rectangular"
              extraClasses={styles["m-loader"]}
            />
            <SkeletonLoader
              height={6}
              rounding="sm"
              variant="rectangular"
              extraClasses={styles["xs-loader"]}
            />
          </div>
          <div className={styles.fromAccount}>
            <SkeletonLoader
              height={6}
              rounding="sm"
              variant="rectangular"
              extraClasses={styles["xxs-loader"]}
            />
            <div className={styles.accountDetails}>
              <SkeletonLoader
                height={6}
                rounding="sm"
                variant="rectangular"
                extraClasses={styles["m-loader"]}
              />
              <SkeletonLoader width={6} height={6} variant="circular" />
            </div>
          </div>
          <div className={styles.toAccount}>
            <SkeletonLoader
              height={6}
              rounding="sm"
              variant="rectangular"
              extraClasses={styles["xxs-loader"]}
            />
            <div className={styles.accountDetails}>
              <SkeletonLoader
                height={6}
                rounding="sm"
                variant="rectangular"
                extraClasses={styles["m-loader"]}
              />
              <SkeletonLoader width={6} height={6} variant="circular" />
            </div>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className={styles.loadingSkeleton}>
      <table className={clsx(styles.table, styles.skeletonTable)}>
        <thead>
          <tr>
            <th>{t("transactions.history.table.time")}</th>
            <th>{t("transactions.history.table.type")}</th>
            <th>{t("transactions.history.table.order_number")}</th>
            <th>{t("transactions.history.table.from")}</th>
            <th>{t("transactions.history.table.to")}</th>
            <th>{t("transactions.history.table.amount")}</th>
            <th>{t("transactions.history.table.status")}</th>
            <th>{t("transactions.history.table.action")}</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 10 }).map((_, index) => (
            <tr key={index}>
              <td>
                <SkeletonLoader
                  height={6}
                  rounding="sm"
                  variant="rectangular"
                  extraClasses={styles["xl-loader"]}
                />
              </td>
              <td>
                <SkeletonLoader
                  height={6}
                  rounding="sm"
                  variant="rectangular"
                  extraClasses={styles["xs-loader"]}
                />
              </td>
              <td>
                <SkeletonLoader
                  height={6}
                  rounding="sm"
                  variant="rectangular"
                  extraClasses={styles["l-loader"]}
                />
              </td>
              <td>
                <div className={styles.loaders}>
                  <SkeletonLoader height={6} width={6} variant="circular" />
                  <SkeletonLoader
                    height={6}
                    rounding="sm"
                    variant="rectangular"
                    extraClasses={styles["m-loader"]}
                  />
                </div>
              </td>
              <td>
                <div className={styles.loaders}>
                  <SkeletonLoader height={6} width={6} variant="circular" />
                  <SkeletonLoader
                    height={6}
                    rounding="sm"
                    variant="rectangular"
                    extraClasses={styles["m-loader"]}
                  />
                </div>
              </td>
              <td>
                <SkeletonLoader
                  height={6}
                  rounding="sm"
                  variant="rectangular"
                  extraClasses={styles["l-loader"]}
                />
              </td>
              <td>
                <SkeletonLoader
                  height={6}
                  rounding="sm"
                  variant="rectangular"
                  extraClasses={styles["l-loader"]}
                />
              </td>
              <td>
                <SkeletonLoader
                  height={6}
                  rounding="sm"
                  variant="rectangular"
                  extraClasses={styles["s-loader"]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
